import {
  HTMLAttributes,
  ReactNode,
  forwardRef,
  useEffect,
  useRef,
  useState,
} from "react";
import styled from "@emotion/styled";

const Container = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  flex: 1;
`;

const FadeMask = styled.div<{
  position: "top" | "bottom";
  $show: boolean;
}>`
  position: absolute;
  left: 0;
  right: 0;
  height: 2.5rem;
  pointer-events: none;
  z-index: 2;
  transition: opacity 0.2s;
  opacity: ${({ $show }) => ($show ? 1 : 0)};
  background: ${({ position, theme }) =>
    position === "top"
      ? `linear-gradient(to bottom, ${theme?.colors?.background?.default || "#fff"} 75%, transparent)`
      : `linear-gradient(to top, ${theme?.colors?.background?.default || "#fff"} 75%, transparent)`};
  top: ${({ position }) => (position === "top" ? 0 : "unset")};
  bottom: ${({ position }) => (position === "bottom" ? 0 : "unset")};
`;

const ScrollArea = styled.div`
  flex: 1;
  height: 100%;
  overflow-y: auto;
  position: relative;
  z-index: 1;
`;

// Props: children is the scrollable content
export interface FadingScrollContainerProps
  extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
  className?: string;
}

export const FadingScrollContainer = forwardRef<
  HTMLDivElement,
  FadingScrollContainerProps
>(({ children, className, ...props }, ref) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [showTopFade, setShowTopFade] = useState(false);
  const [showBottomFade, setShowBottomFade] = useState(true);

  // Allow ref forwarding
  useEffect(() => {
    if (typeof ref === "function") {
      ref(containerRef.current);
    } else if (ref) {
      (ref as React.MutableRefObject<HTMLDivElement | null>).current =
        containerRef.current;
    }
  }, [containerRef.current, ref]);

  const handleScroll = () => {
    const container = containerRef.current;
    if (!container) return;
    setShowTopFade(container.scrollTop > 10);
    const isAtBottom =
      Math.abs(
        container.scrollHeight - container.scrollTop - container.clientHeight
      ) < 10;
    setShowBottomFade(!isAtBottom);
  };

  useEffect(() => {
    handleScroll();
    const container = containerRef.current;
    if (container) {
      container.addEventListener("scroll", handleScroll);
      return () => {
        container.removeEventListener("scroll", handleScroll);
      };
    }
  }, []);

  return (
    <Container className={className} {...props}>
      <FadeMask position="top" $show={showTopFade} />
      <ScrollArea ref={containerRef}>{children}</ScrollArea>
      <FadeMask position="bottom" $show={showBottomFade} />
    </Container>
  );
});

FadingScrollContainer.displayName = "FadingScrollContainer";
